import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, Trash2Icon, ImageIcon, CheckCircleIcon } from 'lucide-react';
interface Option {
  id: string;
  name: string;
  values: string;
}
interface Modifier {
  id: string;
  name: string;
  price: number;
}
export function ProductEdit() {
  const navigate = useNavigate();
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const [params] = useSearchParams();
  const shopId = params.get('shop') || '';
  const isNew = productId === 'new';
  const [name, setName] = useState(isNew ? '' : 'Swim Shorts');
  const [description, setDescription] = useState(isNew ? '' : 'Quick-dry swim shorts for men, made from recycled polyester.');
  const [sku, setSku] = useState(isNew ? '' : 'SS-003');
  const [category, setCategory] = useState(isNew ? 'Swimwear' : 'Swimwear');
  const [price, setPrice] = useState(isNew ? '0.00' : '34.99');
  const [salePrice, setSalePrice] = useState(isNew ? '' : '29.99');
  const [stockQuantity, setStockQuantity] = useState(isNew ? '0' : '28');
  const [imageUrl, setImageUrl] = useState(isNew ? '' : 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80');
  const [isActive, setIsActive] = useState(true);
  const [options, setOptions] = useState<Option[]>(isNew ? [] : [{
    id: 'o1',
    name: 'Size',
    values: 'S, M, L, XL'
  }, {
    id: 'o2',
    name: 'Color',
    values: 'Navy, Coral, Black'
  }]);
  const [modifiers, setModifiers] = useState<Modifier[]>(isNew ? [] : [{
    id: 'm1',
    name: 'Express delivery',
    price: 5.0
  }]);
  const [savedToast, setSavedToast] = useState(false);
  const addOption = () => setOptions([...options, {
    id: 'o' + Math.random().toString(36).slice(2, 7),
    name: '',
    values: ''
  }]);
  const removeOption = (id: string) => setOptions(options.filter(o => o.id !== id));
  const addModifier = () => setModifiers([...modifiers, {
    id: 'm' + Math.random().toString(36).slice(2, 7),
    name: '',
    price: 0
  }]);
  const removeModifier = (id: string) => setModifiers(modifiers.filter(m => m.id !== id));
  const handleSave = () => {
    setSavedToast(true);
    setTimeout(() => {
      setSavedToast(false);
      navigate(shopId ? `/shop-details?id=${shopId}` : '/shops');
    }, 1200);
  };
  return <div className="max-w-[1200px] mx-auto space-y-6" data-id="element-11519">
      <div data-id="element-11520">
        <button onClick={() => navigate(shopId ? `/shop-details?id=${shopId}` : '/shops')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-3" data-id="element-11521">
          <ArrowLeftIcon size={14} className="mr-1.5" data-id="element-11522" />
          Back to shop
        </button>
        <div className="flex items-center justify-between" data-id="element-11523">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-11524">
            {isNew ? 'New product' : 'Edit product'}
          </h1>
          <div className="flex items-center gap-2" data-id="element-11525">
            <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-11526">
              Cancel
            </button>
            <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm" data-id="element-11527">
              Save product
            </button>
          </div>
        </div>
      </div>

      {savedToast && <div className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm shadow-lg flex items-center gap-2 z-50" data-id="element-11528">
          <CheckCircleIcon size={16} className="text-green-400" data-id="element-11529" />
          Product saved
        </div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-11530">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6" data-id="element-11531">
          {/* Basic info */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-11532">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11533">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-11534">
                Basic information
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4" data-id="element-11535">
              <div data-id="element-11536">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11537">
                  Name <span className="text-red-400" data-id="element-11538">*</span>
                </label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11539" />
              </div>
              <div data-id="element-11540">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11541">
                  Description
                </label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11542" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-11543">
                <div data-id="element-11544">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11545">
                    SKU
                  </label>
                  <input type="text" value={sku} onChange={e => setSku(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11546" />
                </div>
                <div data-id="element-11547">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11548">
                    Category
                  </label>
                  <input type="text" value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11549" />
                </div>
              </div>
            </div>
          </div>

          {/* Pricing & inventory */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-11550">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11551">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-11552">
                Pricing & inventory
              </h2>
            </div>
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-11553">
              <div data-id="element-11554">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11555">
                  Regular price (MVR) <span className="text-red-400" data-id="element-11556">*</span>
                </label>
                <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11557" />
              </div>
              <div data-id="element-11558">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11559">
                  Sale price (MVR)
                </label>
                <input type="number" step="0.01" value={salePrice} onChange={e => setSalePrice(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11560" />
                <p className="text-xs text-gray-400 mt-1" data-id="element-11561">
                  Leave blank for no discount
                </p>
              </div>
              <div data-id="element-11562">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11563">
                  Stock quantity
                </label>
                <input type="number" value={stockQuantity} onChange={e => setStockQuantity(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11564" />
              </div>
            </div>
          </div>

          {/* Product options */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-11565">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-11566">
              <div data-id="element-11567">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-11568">
                  Product options
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-11569">
                  Variants the customer must pick from, e.g. Size or Color.
                </p>
              </div>
              <button onClick={addOption} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-11570">
                <PlusIcon size={14} data-id="element-11571" />
                Add option
              </button>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-11572">
              {options.map((opt, i) => <div key={opt.id} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-3 items-end" data-id="element-11573">
                  <div data-id="element-11574">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-11575">
                      Option name
                    </label>
                    <input type="text" value={opt.name} onChange={e => setOptions(options.map(o => o.id === opt.id ? {
                  ...o,
                  name: e.target.value
                } : o))} placeholder="e.g. Size" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11576" />
                  </div>
                  <div data-id="element-11577">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-11578">
                      Values (comma separated)
                    </label>
                    <input type="text" value={opt.values} onChange={e => setOptions(options.map(o => o.id === opt.id ? {
                  ...o,
                  values: e.target.value
                } : o))} placeholder="e.g. S, M, L, XL" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11579" />
                  </div>
                  <button onClick={() => removeOption(opt.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Remove option" data-id="element-11580">
                    <Trash2Icon size={16} data-id="element-11581" />
                  </button>
                </div>)}
              {options.length === 0 && <p className="text-sm text-gray-500 text-center py-3" data-id="element-11582">
                  No options yet — add one to let customers pick a variant.
                </p>}
            </div>
          </div>

          {/* Modifiers */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-11583">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-11584">
              <div data-id="element-11585">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-11586">
                  Modifiers
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-11587">
                  Optional add-ons with their own price (e.g. gift wrap,
                  monogram).
                </p>
              </div>
              <button onClick={addModifier} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-11588">
                <PlusIcon size={14} data-id="element-11589" />
                Add modifier
              </button>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-11590">
              {modifiers.map(m => <div key={m.id} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-3 items-end" data-id="element-11591">
                  <div data-id="element-11592">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-11593">
                      Modifier name
                    </label>
                    <input type="text" value={m.name} onChange={e => setModifiers(modifiers.map(x => x.id === m.id ? {
                  ...x,
                  name: e.target.value
                } : x))} placeholder="e.g. Gift wrap" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11594" />
                  </div>
                  <div data-id="element-11595">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-11596">
                      Extra price (MVR)
                    </label>
                    <input type="number" step="0.01" value={m.price} onChange={e => setModifiers(modifiers.map(x => x.id === m.id ? {
                  ...x,
                  price: Number(e.target.value)
                } : x))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11597" />
                  </div>
                  <button onClick={() => removeModifier(m.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Remove modifier" data-id="element-11598">
                    <Trash2Icon size={16} data-id="element-11599" />
                  </button>
                </div>)}
              {modifiers.length === 0 && <p className="text-sm text-gray-500 text-center py-3" data-id="element-11600">
                  No modifiers yet — add one to offer paid extras.
                </p>}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6" data-id="element-11601">
          {/* Image */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-11602">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-11603">
              <ImageIcon size={16} className="text-gray-500" data-id="element-11604" />
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-11605">
                Product image
              </h2>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-11606">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200" data-id="element-11607">
                {imageUrl ? <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" data-id="element-11608" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-11609">
                    <ImageIcon size={32} data-id="element-11610" />
                  </div>}
              </div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-11611">
                Image URL
              </label>
              <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-11612" />
            </div>
          </div>

          {/* Visibility */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-11613">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11614">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-11615">
                Visibility
              </h2>
            </div>
            <div className="px-6 py-5" data-id="element-11616">
              <label className="flex items-center cursor-pointer" data-id="element-11617">
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="sr-only" data-id="element-11618" />
                <div className={`relative w-9 h-5 rounded-full transition-colors ${isActive ? 'bg-[#E2231A]' : 'bg-gray-200'}`} data-id="element-11619">
                  <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${isActive ? 'translate-x-4' : ''}`} data-id="element-11620" />
                </div>
                <span className="ml-3 text-sm text-gray-700" data-id="element-11621">
                  {isActive ? 'Active — visible in shop' : 'Inactive — hidden from shop'}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>;
}